import React, { useRef, Children } from 'react';
import { GraduationCapIcon, TrendingUpIcon, UsersIcon, ArrowRightIcon } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
export function HomeWhyHIRE() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  // Parallax for background elements
  const y1 = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const y2 = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.5, 1, 1, 0.5]);
  const differentiators = [{
    icon: GraduationCapIcon,
    title: 'We Train, You Lead',
    description: 'Every Monday and Tuesday, we run a live, full-day Sales School for your rookies. We provide the rocket fuel and the kick-start, saving you 15+ hours of training time per recruit. You get them back ready to work.'
  }, {
    icon: TrendingUpIcon,
    title: 'Continuous Innovation',
    description: 'The system never stays still. We constantly refine our scripts, funnels, and training based on real-time market data. You always get the latest version of what works—without having to build it yourself.'
  }, {
    icon: UsersIcon,
    title: 'Active Partnership',
    description: "We don't just hand over a login. We act as active consultants, tracking your KPIs and coaching your team to ensure the system delivers math-based results. We are in the trenches with you."
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section ref={sectionRef} className="section-padding bg-white relative overflow-hidden" data-id="element-578">
      {/* Subtle background decoration */}
      <motion.div className="absolute top-0 right-0 w-96 h-96 bg-mint/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" style={{
      y: y1
    }} data-id="element-579" />
      <motion.div className="absolute bottom-0 left-0 w-64 h-64 bg-deep-blue/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" style={{
      y: y2
    }} data-id="element-580" />

      <div className="container-custom relative z-10" data-id="element-581">
        <motion.div className="max-w-3xl mx-auto text-center mb-10 md:mb-12 lg:mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.5
      }} style={{
        opacity
      }} data-id="element-582">
          <h2 className="mb-3 md:mb-4 lg:mb-6" data-id="element-583">Why HIRE?</h2>
          <p className="text-body-large leading-relaxed" data-id="element-584">
            You don't need more effort. You need a partner that does the heavy
            lifting for you.
          </p>
        </motion.div>

        <motion.div className="grid md:grid-cols-3 gap-4 md:gap-6 lg:gap-8 max-w-6xl mx-auto mb-8 md:mb-10 lg:mb-12" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-585">
          {differentiators.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={index} className="bg-gray-50 rounded-xl p-5 md:p-6 lg:p-8 shadow-lg border-2 border-gray-200 hover:border-mint transition-all group" variants={itemVariants} whileHover={{
            y: -5,
            boxShadow: '0 20px 40px -15px rgba(127, 224, 195, 0.3)',
            transition: {
              duration: 0.2
            }
          }} data-id="element-586">
                <motion.div className="w-10 h-10 md:w-12 md:h-12 bg-mint/10 rounded-lg flex items-center justify-center mb-3 md:mb-4" whileHover={{
              scale: 1.1,
              rotate: 5
            }} transition={{
              type: 'spring',
              stiffness: 400
            }} data-id="element-587">
                  <Icon className="h-5 w-5 md:h-6 md:w-6 text-mint" data-id="element-588" />
                </motion.div>
                <h3 className="mb-2 md:mb-3 text-deep-blue" data-id="element-589">{item.title}</h3>
                <p className="leading-relaxed text-gray-600" data-id="element-590">
                  {item.description}
                </p>
              </motion.div>;
        })}
        </motion.div>

        <motion.div className="text-center" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.4
      }} data-id="element-591">
          <a href="/about" className="inline-flex items-center text-mint font-bold hover:text-mint-dark transition-colors text-sm group" data-id="element-592">
            Read Our Story{' '}
            <ArrowRightIcon className="ml-2 h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform" data-id="element-593" />
          </a>
        </motion.div>
      </div>
    </section>;
}